<template>
  <VaDropdown class="workspace-switcher" placement="bottom-start" :offset="[0, 10]" stick-to-edges>
    <template #anchor>
      <button
        class="ws-trigger"
        :class="isDarkTheme ? 'ws-trigger--dark' : 'ws-trigger--light'"
        :aria-label="t('workspace.switchLabel')"
        :title="t('workspace.switchLabel')"
      >
        <span class="material-icons ws-trigger__icon">{{ current.icon }}</span>
        <span class="ws-trigger__label">{{ current.label }}</span>
        <span class="material-icons ws-trigger__chevron">unfold_more</span>
      </button>
    </template>

    <div class="ws-menu" :class="isDarkTheme ? 'ws-menu--dark' : 'ws-menu--light'">
      <div class="ws-menu__header">{{ t('workspace.switchLabel') }}</div>
      <button
        v-for="ws in workspaces"
        :key="ws.key"
        class="ws-menu__item"
        :class="{ 'ws-menu__item--active': ws.key === currentWorkspace }"
        @click="select(ws)"
      >
        <span class="material-icons ws-menu__item-icon">{{ ws.icon }}</span>
        <span class="ws-menu__item-label">{{ ws.label }}</span>
        <span v-if="ws.key === currentWorkspace" class="material-icons ws-menu__item-check">check</span>
      </button>
    </div>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user-store'
import { WORKSPACES, type WorkspaceDef } from '@/constants/workspaces'
import { useEffectiveWorkspace } from '@/composables/useEffectiveWorkspace'

const router = useRouter()
const store = useStore()
const { t } = useI18n()
const userStore = useUserStore()

interface WorkspaceOption extends WorkspaceDef {
  label: string
}

// AI Agents é interno: só aparece para quem tem papel no módulo (ou admin).
const workspaces = computed<WorkspaceOption[]>(() =>
  WORKSPACES.filter((ws) => ws.key !== 'ai-agents' || userStore.aiAgentsAtLeast('viewer')).map((ws) => ({
    ...ws,
    label: t(ws.labelKey),
  })),
)

const currentWorkspace = useEffectiveWorkspace()
const current = computed(() => workspaces.value.find((w) => w.key === currentWorkspace.value) ?? workspaces.value[0])

function select(ws: WorkspaceOption) {
  store.commit('setWorkspace', ws.key)
  if (ws.key !== currentWorkspace.value || router.currentRoute.value.name !== ws.home) {
    router.push({ name: ws.home }).catch(() => {})
  }
}

// ── Theme sync (mesmo padrão do navbar/sidebar) ──
const currentTheme = ref('light')
function syncTheme() {
  try {
    if (typeof window !== 'undefined' && (window as any).getAppTheme) {
      currentTheme.value = (window as any).getAppTheme() ?? 'light'
    }
  } catch (e) {
    /* ignore */
  }
}
const isDarkTheme = computed(() => currentTheme.value === 'dark')
onMounted(() => {
  syncTheme()
  window.addEventListener('app-theme-changed', syncTheme)
})
onBeforeUnmount(() => window.removeEventListener('app-theme-changed', syncTheme))
</script>

<style scoped lang="scss">
.ws-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid var(--shell-glass-border);
  background: var(--shell-input-bg);
  color: var(--shell-text);
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: var(--shell-item-hover);
    border-color: var(--color-brand-accent, #fa2b00);
  }
  &:focus-visible {
    outline: 2px solid #ff4d2e;
    outline-offset: 2px;
  }

  &__icon {
    font-size: 18px;
    color: var(--color-brand-accent, #fa2b00);
  }
  &__label {
    white-space: nowrap;
  }
  &__chevron {
    font-size: 16px;
    color: var(--shell-text-muted);
  }
}

.ws-menu {
  min-width: 210px;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid var(--shell-glass-border);
  background: var(--shell-glass-bg, #fff);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.35);

  // O VaDropdown teleporta o menu pra fora do container temático, então as variáveis
  // --shell-* não chegam aqui — fixamos as cores por variante explicitamente.
  &--dark {
    background: #1c1c1c;
    border-color: rgba(255, 255, 255, 0.12);

    .ws-menu__header {
      color: rgba(255, 255, 255, 0.6);
    }
    .ws-menu__item {
      color: #f2f2f2;
    }
    .ws-menu__item-icon {
      color: rgba(255, 255, 255, 0.7);
    }
    .ws-menu__item:hover {
      background: rgba(255, 255, 255, 0.08);
    }
    .ws-menu__item--active {
      background: rgba(250, 43, 0, 0.2);
      color: #ff5a38;
      .ws-menu__item-icon,
      .ws-menu__item-check {
        color: #ff5a38;
      }
    }
  }
  &--light {
    background: #fff;

    .ws-menu__header {
      color: #6b6b6b;
    }
    .ws-menu__item {
      color: #1c1c1c;
    }
    .ws-menu__item-icon {
      color: #8a8a8a;
    }
    .ws-menu__item:hover {
      background: rgba(0, 0, 0, 0.05);
    }
    .ws-menu__item--active {
      background: rgba(250, 43, 0, 0.12);
      color: #fa2b00;
      .ws-menu__item-icon,
      .ws-menu__item-check {
        color: #fa2b00;
      }
    }
  }

  &__header {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--shell-text-muted);
    padding: 6px 10px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 10px;
    border: none;
    background: none;
    border-radius: 9px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    font-size: 13.5px;
    color: var(--shell-text);
    transition: background 0.15s;

    &:hover {
      background: var(--shell-item-hover);
    }
    &--active {
      background: rgba(250, 43, 0, 0.12);
      color: var(--color-brand-accent, #fa2b00);
      font-weight: 600;
      .ws-menu__item-icon {
        color: var(--color-brand-accent, #fa2b00);
      }
    }

    &-icon {
      font-size: 19px;
      color: var(--shell-text-muted);
    }
    &-label {
      flex: 1;
    }
    &-check {
      font-size: 17px;
      color: var(--color-brand-accent, #fa2b00);
    }
  }
}
</style>
